<template>
  <div class="room-design-page">
    <div class="container flex flex-col gap-5 p-5 h-full">
      <div class="flex items-center justify-between mb-2">
        <h2 class="page-title">{{ $t('home.roomDesign') }}</h2>
        <el-button
          type="primary"
          class="add-bg-btn"
          icon="el-icon-plus"
          @click="openAdd"
        >
          {{ $t('customBackground.add') }}
        </el-button>
      </div>

      <div class="room-design-container">
        <div v-loading="loading" element-loading-background="transparent" class="loading-wrapper">
          <div v-if="!loading && list.length === 0" class="empty-state">
            <div class="empty-icon-wrap">
              <i class="el-icon-picture-outline"></i>
            </div>
            <p class="empty-text">{{ $t('customBackground.empty') }}</p>
            <el-button type="primary" class="add-bg-btn" icon="el-icon-plus" @click="openAdd">
              {{ $t('customBackground.add') }}
            </el-button>
          </div>

          <div v-else class="bg-grid">
            <div v-for="item in list" :key="item.id" class="bg-card">
              <div class="bg-media-wrap">
                <img v-if="item.mediaType === 'image'" :src="item.fileUrl" class="bg-media" alt="" />
                <video v-else :src="item.fileUrl" class="bg-media" muted preload="metadata" />
                <div class="bg-media-overlay"></div>
                <span class="media-tag" :class="'tag-' + item.mediaType">
                  <span class="status-dot"></span>
                  {{ item.mediaType === 'video' ? $t('customBackground.video') : $t('customBackground.image') }}
                </span>
              </div>
              <div class="bg-name" :title="item.name">{{ item.name }}</div>
              <div class="bg-actions">
                <el-button size="mini" type="text" class="preview-text-btn" icon="el-icon-view" @click="openPreview(item)">
                  {{ $t('customBackground.preview') }}
                </el-button>
                <el-button size="mini" type="text" class="edit-btn" icon="el-icon-edit" @click="openEdit(item)">
                  {{ $t('customBackground.edit') }}
                </el-button>
                <el-button size="mini" type="text" class="delete-btn" icon="el-icon-delete" @click="handleDelete(item)">
                  {{ $t('customBackground.delete') }}
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <el-dialog
      :title="dialogMode === 'add' ? $t('customBackground.add') : $t('customBackground.edit')"
      :visible.sync="dialogVisible"
      width="520px"
      :close-on-click-modal="false"
      custom-class="custom-bg-dialog"
      @closed="resetForm"
    >
      <el-form ref="formRef" :model="form" :rules="rules" label-width="100px">
        <el-form-item :label="$t('customBackground.name')" prop="name">
          <el-input
            v-model="form.name"
            :placeholder="$t('customBackground.namePlaceholder')"
            maxlength="128"
            show-word-limit
          />
        </el-form-item>
        <el-form-item :label="$t('customBackground.file')" prop="fileUrl">
          <div class="upload-area">
            <el-upload
              :show-file-list="false"
              :http-request="handleUpload"
              :before-upload="beforeUpload"
              accept=".png,.jpg,.jpeg,.mp4"
            >
              <div v-if="form.fileUrl" class="upload-preview">
                <img v-if="form.mediaType === 'image'" :src="form.fileUrl" class="upload-media" alt="" />
                <video v-else :src="form.fileUrl" class="upload-media" controls />
                <div class="upload-mask">
                  <i class="el-icon-upload2"></i>
                  <span>{{ $t('customBackground.reupload') }}</span>
                </div>
              </div>
              <div v-else class="upload-placeholder">
                <i class="el-icon-plus upload-plus-icon"></i>
                <div class="upload-placeholder-text">{{ $t('customBackground.uploadTip') }}</div>
              </div>
            </el-upload>
            <div class="upload-hint">{{ $t('customBackground.uploadHint') }}</div>
            <div class="upload-progress-container">
              <el-progress v-if="uploading" :percentage="uploadProgress" :stroke-width="4" />
            </div>
          </div>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" :loading="saving" @click="submit">{{ $t('common.confirm') }}</el-button>
      </span>
    </el-dialog>

    <el-dialog
      :title="previewItem ? previewItem.name : $t('customBackground.preview')"
      :visible.sync="previewVisible"
      width="720px"
      custom-class="custom-bg-preview-dialog"
    >
      <div v-if="previewItem" class="preview-body">
        <img v-if="previewItem.mediaType === 'image'" :src="previewItem.fileUrl" class="preview-full" alt="" />
        <video v-else :src="previewItem.fileUrl" class="preview-full" controls autoplay />
      </div>
    </el-dialog>
  </div>
</template>

<script>
import api from '@/network/customBackground/index'

const defaultForm = () => ({
  id: null,
  name: '',
  fileUrl: '',
  mediaType: ''
})

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg']

export default {
  name: 'RoomDesign',
  data() {
    return {
      list: [],
      loading: false,
      dialogVisible: false,
      dialogMode: 'add',
      saving: false,
      uploading: false,
      uploadProgress: 0,
      form: defaultForm(),
      previewVisible: false,
      previewItem: null
    }
  },
  computed: {
    rules() {
      return {
        name: [{ required: true, message: this.$t('customBackground.nameRequired'), trigger: 'blur' }],
        fileUrl: [{ required: true, message: this.$t('customBackground.fileRequired'), trigger: 'change' }]
      }
    }
  },
  created() {
    this.fetchList()
  },
  methods: {
    async fetchList() {
      this.loading = true
      try {
        const res = await api.listCustomBackgrounds()
        this.list = res.data || []
      } catch (e) {
        this.$message.error(this.$t('customBackground.loadError'))
      } finally {
        this.loading = false
      }
    },
    openAdd() {
      this.dialogMode = 'add'
      this.resetForm()
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
    },
    openEdit(row) {
      this.dialogMode = 'edit'
      this.form = {
        id: row.id,
        name: row.name || '',
        fileUrl: row.fileUrl || '',
        mediaType: row.mediaType || ''
      }
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
    },
    resetForm() {
      this.form = defaultForm()
      this.uploading = false
      this.uploadProgress = 0
    },
    beforeUpload(file) {
      const ext = file.name.split('.').pop().toLowerCase()
      const allowed = [...IMAGE_EXTENSIONS, 'mp4']
      if (!allowed.includes(ext)) {
        this.$message.error(this.$t('customBackground.formatError'))
        return false
      }
      const isImage = IMAGE_EXTENSIONS.includes(ext)
      const maxSizeMb = 10
      if (file.size / 1024 / 1024 > maxSizeMb) {
        this.$message.error(this.$t('customBackground.sizeError', { size: maxSizeMb }))
        return false
      }
      return true
    },
    async handleUpload({ file }) {
      this.uploading = true
      this.uploadProgress = 0
      try {
        const res = await api.uploadCustomBackground(file, (percent) => {
          this.uploadProgress = percent
        })
        this.form.fileUrl = res.data.fileUrl
        this.form.mediaType = res.data.mediaType
        this.$refs.formRef && this.$refs.formRef.validateField('fileUrl')
        this.$message.success(this.$t('customBackground.uploadSuccess'))
      } catch (e) {
        /* request 拦截器已提示 */
      } finally {
        this.uploading = false
      }
    },
    submit() {
      this.$refs.formRef.validate(async (valid) => {
        if (!valid) return
        this.saving = true
        try {
          const payload = {
            name: this.form.name.trim(),
            fileUrl: this.form.fileUrl,
            mediaType: this.form.mediaType
          }
          if (this.dialogMode === 'edit') {
            payload.id = this.form.id
            await api.updateCustomBackground(payload)
          } else {
            await api.addCustomBackground(payload)
          }
          this.$message.success(this.$t('customBackground.saveSuccess'))
          this.dialogVisible = false
          await this.fetchList()
        } catch (e) {
          /* request 拦截器已提示 */
        } finally {
          this.saving = false
        }
      })
    },
    handleDelete(row) {
      this.$confirm(this.$t('customBackground.confirmDelete'), this.$t('customBackground.tip'), {
        type: 'warning',
        confirmButtonText: this.$t('common.confirm'),
        cancelButtonText: this.$t('common.cancel')
      })
        .then(async () => {
          await api.deleteCustomBackground(row.id)
          this.$message.success(this.$t('customBackground.deleteSuccess'))
          await this.fetchList()
        })
        .catch(() => {})
    },
    openPreview(item) {
      this.previewItem = item
      this.previewVisible = true
    },
    applyBackground(item) {
      this.$message.success(this.$t('customBackground.applySuccess', { name: item.name }))
    }
  }
}
</script>

<style scoped>
.room-design-page {
  padding: 16px;
  background: radial-gradient(circle at 10% 20%, rgba(99, 102, 241, 0.08) 0%, rgba(15, 23, 42, 0) 40%), #0f172a;
  height: 100%;
  box-sizing: border-box;
  color: #f1f5f9;
  display: flex;
  flex-direction: column;
}
.page-title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.025em;
  background: linear-gradient(135deg, #ffffff 0%, #cbd5e1 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}
.add-bg-btn {
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%) !important;
  border: none !important;
  border-radius: 12px !important;
  box-shadow: 0 4px 14px 0 rgba(99, 102, 241, 0.3) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  font-weight: 600 !important;
  padding: 10px 20px !important;
}
.add-bg-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px 0 rgba(99, 102, 241, 0.45) !important;
  background: linear-gradient(135deg, #818cf8 0%, #6366f1 100%) !important;
}
.add-bg-btn:active {
  transform: translateY(1px);
}

.room-design-container {
  background: rgba(30, 41, 59, 0.45);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 24px;
  border-radius: 20px;
  flex: 1;
  overflow: hidden;
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
}

.loading-wrapper {
  height: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
}

.bg-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  grid-auto-rows: min-content;
  align-content: start;
  gap: 24px;
  overflow-y: auto;
  flex: 1;
  padding: 4px;
}
.bg-grid::-webkit-scrollbar {
  width: 6px;
}
.bg-grid::-webkit-scrollbar-track {
  background: rgba(15, 23, 42, 0.4);
  border-radius: 4px;
}
.bg-grid::-webkit-scrollbar-thumb {
  background: rgba(99, 102, 241, 0.3);
  border-radius: 4px;
}
.bg-grid::-webkit-scrollbar-thumb:hover {
  background: rgba(99, 102, 241, 0.5);
}

.bg-card {
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.55) 0%, rgba(15, 23, 42, 0.75) 100%);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.05);
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}
.bg-card:hover {
  border-color: rgba(99, 102, 241, 0.35);
  transform: translateY(-6px) scale(1.01);
  box-shadow: 0 20px 40px rgba(99, 102, 241, 0.12), 
              0 0 0 1px rgba(99, 102, 241, 0.2), 
              inset 0 1px 0 rgba(255, 255, 255, 0.1);
}

.bg-media-wrap {
  position: relative;
  height: 160px;
  background: #0f172a;
  overflow: hidden;
}
.bg-media {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s cubic-bezier(0.25, 0.8, 0.25, 1), filter 0.5s ease;
}
.bg-card:hover .bg-media {
  transform: scale(1.08);
  filter: saturate(1.1) brightness(1.05);
}
.bg-media-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, transparent 50%, rgba(15, 23, 42, 0.85) 100%);
  pointer-events: none;
  z-index: 1;
}

.media-tag {
  position: absolute;
  top: 12px;
  right: 12px;
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 8px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(15, 23, 42, 0.6) !important;
  backdrop-filter: blur(8px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  z-index: 2;
}
.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
}
.tag-image {
  color: #38bdf8 !important;
  border: 1px solid rgba(56, 189, 248, 0.35) !important;
}
.tag-image .status-dot {
  background-color: #38bdf8;
  box-shadow: 0 0 6px #38bdf8;
}
.tag-video {
  color: #a78bfa !important;
  border: 1px solid rgba(139, 92, 246, 0.35) !important;
}
.tag-video .status-dot {
  background-color: #a78bfa;
  box-shadow: 0 0 6px #a78bfa;
}

.bg-name {
  padding: 18px 20px 14px;
  color: #f1f5f9;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.3s ease;
  z-index: 2;
}
.bg-card:hover .bg-name {
  color: #a5b4fc;
}

.bg-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  padding: 14px 20px;
  margin-top: auto;
  position: relative;
}
.bg-actions::before {
  content: '';
  position: absolute;
  top: 0;
  left: 20px;
  right: 20px;
  height: 1px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.08) 50%, rgba(255, 255, 255, 0) 100%);
}
.bg-actions .el-button {
  padding: 0;
  font-size: 12px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin: 0 !important;
}
.bg-actions .el-button i {
  transition: transform 0.3s ease;
}
.bg-actions .el-button:hover i {
  transform: scale(1.18);
}

.preview-text-btn.el-button--text {
  color: #38bdf8 !important;
}
.preview-text-btn.el-button--text:hover {
  color: #7dd3fc !important;
  transform: translateY(-1px);
}
.preview-text-btn:hover i {
  transform: scale(1.22);
}

.edit-btn.el-button--text {
  color: #a78bfa !important;
}
.edit-btn.el-button--text:hover {
  color: #c084fc !important;
  transform: translateY(-1px);
}
.edit-btn:hover i {
  transform: rotate(15deg) scale(1.2);
}

.delete-btn.el-button--text {
  color: #f87171 !important;
}
.delete-btn.el-button--text:hover {
  color: #fca5a5 !important;
  transform: translateY(-1px);
}
.delete-btn:hover i {
  transform: rotate(-10deg) scale(1.2);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: #94a3b8;
  gap: 16px;
}
.empty-icon-wrap {
  background: rgba(99, 102, 241, 0.1);
  border: 1px dashed rgba(99, 102, 241, 0.3);
  border-radius: 50%;
  width: 96px;
  height: 96px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  box-shadow: inset 0 0 20px rgba(99, 102, 241, 0.05);
  animation: float-anim 3s ease-in-out infinite;
}
.empty-icon-wrap i {
  font-size: 44px;
  color: #818cf8;
}
.empty-text {
  font-size: 15px;
  font-weight: 500;
  color: #94a3b8;
  margin: 0;
}
@keyframes float-anim {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}

.upload-area {
  width: 100%;
}
.upload-area ::v-deep .el-upload {
  width: 100%;
  display: block;
}
.upload-placeholder {
  width: 100%;
  height: 160px;
  border: 1px dashed rgba(139, 92, 246, 0.45);
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  background: rgba(15, 23, 42, 0.4);
  color: #94a3b8;
  transition: all 0.3s ease;
  box-shadow: inset 0 0 12px rgba(0, 0, 0, 0.1);
}
.upload-placeholder:hover {
  border-color: #8b5cf6;
  background: rgba(139, 92, 246, 0.05);
  color: #cbd5e1;
}
.upload-plus-icon {
  font-size: 32px;
  color: #818cf8;
  transition: transform 0.3s ease;
}
.upload-placeholder:hover .upload-plus-icon {
  transform: scale(1.1);
  color: #a78bfa;
}
.upload-placeholder-text {
  position: absolute;
  bottom: 24px;
  font-size: 13px;
  font-weight: 500;
}

.upload-preview {
  width: 100%;
  height: 160px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  background: rgba(15, 23, 42, 0.5);
}
.upload-media {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.upload-mask {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(4px);
  color: #f8fafc;
  font-weight: 600;
  font-size: 13px;
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 2;
}
.upload-preview:hover .upload-mask {
  opacity: 1;
}
.upload-mask i {
  font-size: 24px;
  color: #a78bfa;
}
.upload-hint {
  margin-top: 10px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
}
.upload-progress-container {
  margin-top: 12px;
}

.preview-body {
  display: flex;
  justify-content: center;
  background: #090d16;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
}
.preview-full {
  display: block;
  max-width: 100%;
  max-height: 65vh;
  object-fit: contain;
}
</style>

<style>
/* Global overrides for Element UI Dialog inside room design page */
.custom-bg-dialog,
.custom-bg-preview-dialog {
  background: rgba(30, 41, 59, 0.95) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  border-radius: 20px !important;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.7) !important;
}

.custom-bg-dialog .el-dialog__header,
.custom-bg-preview-dialog .el-dialog__header {
  padding: 24px 24px 16px !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06) !important;
}

.custom-bg-dialog .el-dialog__title,
.custom-bg-preview-dialog .el-dialog__title {
  color: #f8fafc !important;
  font-size: 18px !important;
  font-weight: 700 !important;
  letter-spacing: -0.02em;
}

.custom-bg-dialog .el-dialog__headerbtn .el-dialog__close,
.custom-bg-preview-dialog .el-dialog__headerbtn .el-dialog__close {
  color: #94a3b8 !important;
  font-size: 16px;
  transition: color 0.2s;
}
.custom-bg-dialog .el-dialog__headerbtn .el-dialog__close:hover,
.custom-bg-preview-dialog .el-dialog__headerbtn .el-dialog__close:hover {
  color: #f1f5f9 !important;
}

.custom-bg-dialog .el-dialog__body {
  padding: 24px !important;
  color: #e2e8f0 !important;
}

.custom-bg-dialog .el-form-item__label {
  color: #94a3b8 !important;
  font-weight: 500;
}

.custom-bg-dialog .el-input__inner {
  background-color: rgba(15, 23, 42, 0.6) !important;
  border: 1px solid rgba(51, 65, 85, 0.8) !important;
  color: #f8fafc !important;
  border-radius: 8px !important;
  height: 42px !important;
  line-height: 42px !important;
  transition: border-color 0.2s, box-shadow 0.2s !important;
}
.custom-bg-dialog .el-input__inner:focus {
  border-color: #6366f1 !important;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2) !important;
}

.custom-bg-dialog .el-input__count,
.custom-bg-dialog .el-input__count-inner {
  background: transparent !important;
  color: #64748b !important;
}

.custom-bg-dialog .el-dialog__footer {
  padding: 16px 24px 24px !important;
  border-top: 1px solid rgba(255, 255, 255, 0.06) !important;
}

.custom-bg-dialog .el-dialog__footer .el-button {
  border-radius: 10px !important;
  padding: 10px 20px !important;
  font-weight: 600 !important;
  font-size: 13px !important;
}

.custom-bg-dialog .el-dialog__footer .el-button--default {
  background: rgba(51, 65, 85, 0.3) !important;
  border: 1px solid rgba(71, 85, 105, 0.6) !important;
  color: #cbd5e1 !important;
  transition: all 0.2s ease !important;
}
.custom-bg-dialog .el-dialog__footer .el-button--default:hover {
  background: rgba(51, 65, 85, 0.5) !important;
  border-color: rgba(99, 102, 241, 0.4) !important;
  color: #f1f5f9 !important;
}

.custom-bg-dialog .el-dialog__footer .el-button--primary {
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%) !important;
  border: none !important;
  box-shadow: 0 4px 14px rgba(99, 102, 241, 0.3) !important;
  color: #fff !important;
  transition: all 0.2s ease !important;
}
.custom-bg-dialog .el-dialog__footer .el-button--primary:hover {
  box-shadow: 0 6px 20px rgba(99, 102, 241, 0.45) !important;
  transform: translateY(-1px);
}

.upload-progress-container .el-progress-bar__outer {
  background-color: rgba(15, 23, 42, 0.6) !important;
  border-radius: 10px;
}
.upload-progress-container .el-progress-bar__inner {
  background-color: #8b5cf6 !important;
  border-radius: 10px;
}
</style>
